import { useState, useEffect, useCallback, useMemo } from 'react'
import { getProducts, createOrder } from '../../services/api'
import ArticleListing from './ArticleListing'
import Cart from './Cart'
import EditPanel from './EditPanel'
import OrderFooter from './OrderFooter'
import Toast from '../../components/Toast'
import { useToast } from '../../components/Toast'
import { MESSAGES } from '../../constants/messages'

export default function POSPage({ profile, isMobile, showEdit, setShowEdit, showOverview, setShowOverview }) {
  const [products, setProducts] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [cart, setCart] = useState({})
  const [itemsPerRow, setItemsPerRow] = useState(() => {
    const saved = localStorage.getItem(`itemsPerRow_${profile?.id || 'default'}`)
    return saved ? parseInt(saved) : 0
  })
  const [itemSize, setItemSize] = useState(() => {
    const saved = localStorage.getItem(`itemSize_${profile?.id || 'default'}`)
    return saved ? parseInt(saved) : 1
  })
  const [editSaveTrigger, setEditSaveTrigger] = useState(0)
  const [toast, setToast] = useState(null)
  // Live product selection for instant preview
  const [liveSelectedIds, setLiveSelectedIds] = useState(null)
  const [liveSortOrder, setLiveSortOrder] = useState(null)
  const [lastOrderTotal, setLastOrderTotal] = useState(null)

  // Load lastOrderTotal from localStorage when profile.id becomes available
  useEffect(() => {
    if (profile?.id) {
      const saved = localStorage.getItem(`lastOrderTotal_${profile.id}`)
      if (saved) {
        setLastOrderTotal(parseFloat(saved))
      }
    }
  }, [profile?.id])

  const refreshProducts = useCallback(() => {
    if (profile?.id) {
      getProducts(profile.id).then(setProducts).catch(console.error)
    }
  }, [profile])

  // Load all products and profile-filtered products
  useEffect(() => {
    if (profile?.id) {
      // Load all available products
      getProducts().then(setAllProducts).catch(console.error)
      // Load profile-filtered products
      getProducts(profile.id).then(setProducts).catch(console.error)
    }
  }, [profile, editSaveTrigger])

  // Handle live product selection changes from EditPanel
  const handleLiveChange = useCallback((selectedIds, sortOrder) => {
    setLiveSelectedIds(selectedIds)
    setLiveSortOrder(sortOrder)
  }, [])

  // Refresh products without closing edit panel
  const handleRefreshProducts = useCallback(() => {
    setEditSaveTrigger(t => t + 1)
  }, [])

  // Clear live selection when exiting edit mode
  useEffect(() => {
    if (!showEdit) {
      setLiveSelectedIds(null)
      setLiveSortOrder(null)
    }
  }, [showEdit])

  // Compute displayed products based on live selection or API products
  const displayedProducts = useMemo(() => {
    // If we have live selection and are in edit mode, compute from all products
    if (showEdit && liveSelectedIds && allProducts.length > 0) {
      const order = liveSortOrder?.length > 0 ? liveSortOrder : liveSelectedIds
      const productMap = new Map(allProducts.map(p => [p.id, p]))
      return order
        .map(id => productMap.get(id))
        .filter(p => p !== undefined)
    }
    // Otherwise use the API-fetched products
    return products
  }, [showEdit, liveSelectedIds, liveSortOrder, allProducts, products])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 2500)
  }

  // Toast notification styles
  const toastStyle = {
    position: 'fixed', top: '70px', left: '50%', transform: 'translateX(-50%)',
    padding: '12px 24px', borderRadius: '8px', zIndex: 2000,
    background: toast?.type === 'error' ? '#f44336' : '#4CAF50', color: '#fff',
    fontWeight: 'bold', boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
  }

  useEffect(() => {
    localStorage.setItem(`itemsPerRow_${profile?.id || 'default'}`, itemsPerRow.toString())
  }, [itemsPerRow, profile])

  const handleItemsPerRowChange = (value) => {
    setItemsPerRow(value)
  }

  const handleItemSizeChange = (value) => {
    setItemSize(value)
  }

  const addToCart = (product) => {
    setCart((prev) => ({
      ...prev,
      [product.id]: {
        ...product,
        menge: (prev[product.id]?.menge || 0) + 1,
      },
    }))
  }

  const removeFromCart = (productId) => {
    setCart((prev) => {
      const current = prev[productId]
      if (!current) return prev
      
      if (current.menge <= 1) {
        const { [productId]: _, ...rest } = prev
        return rest
      }
      
      return {
        ...prev,
        [productId]: { ...current, menge: current.menge - 1 },
      }
    })
  }

  const clearCart = () => setCart({})

  const total = useMemo(() => {
    return Object.values(cart).reduce((sum, item) => {
      const combinedPrice = (item.preis || 0) + (item.pfand || 0)
      return sum + combinedPrice * item.menge
    }, 0)
  }, [cart])

  const handleOrder = async () => {
    if (Object.keys(cart).length === 0) return
    try {
      const gesamt = total
      
      // Get per-browser print settings from localStorage
      const printEnabled = localStorage.getItem('printEnabled_browser') === 'true'
      const selectedPrinter = localStorage.getItem('selectedPrinter_browser') || ''
      
      await createOrder({
        cart: Object.values(cart).map(item => ({
          id: item.id,
          name: item.name,
          preis: item.preis,
          menge: item.menge,
          pfand: item.pfand || 0,
          print_bon: item.print_bon !== false,
        })),
        stand: profile.name,
        gesamt,
        // Include per-browser print settings with order
        print_enabled: printEnabled,
        printer_id: selectedPrinter,
      })
      clearCart()
      setLastOrderTotal(gesamt)
      if (profile?.id) {
        localStorage.setItem(`lastOrderTotal_${profile.id}`, gesamt.toString())
      }
      showToast(MESSAGES.ORDER_SUCCESS)
    } catch (err) {
      console.error('Order failed:', err)
      showToast(MESSAGES.ORDER_FAILED, 'error')
    }
  }

  // Mobile: show cart when overview is true
  if (isMobile && showOverview) {
    return (
      <div style={{ position: 'relative' }}>
        <Toast toast={toast} />
        <Cart
          cart={cart}
          onAdd={addToCart}
          onRemove={removeFromCart}
          onClear={() => { clearCart(); setShowOverview(false); }}
          onOrder={() => { handleOrder(); setShowOverview(false); }}
          total={total}
          showHeader={false}
          isOverlay={true}
          onBack={() => setShowOverview(false)}
          lastOrderTotal={lastOrderTotal}
        />
      </div>
    )
  }

  // Mobile: show edit panel
  if (isMobile && showEdit) {
    return (
      <div style={{ position: 'relative' }}>
        <Toast toast={toast} />
        <EditPanel 
          profile={profile} 
          onSave={() => { setShowEdit(false); setEditSaveTrigger(t => t + 1); }} 
          onCancel={() => setShowEdit(false)} 
          onSaveEdit={() => { setShowEdit(false); setEditSaveTrigger(t => t + 1); }} 
          onItemsPerRowChange={handleItemsPerRowChange}
          onItemSizeChange={handleItemSizeChange}
          initialItemsPerRow={itemsPerRow}
          isOverlay={true}
          onLiveChange={handleLiveChange}
          onRefresh={handleRefreshProducts}
        />
      </div>
    )
  }

  // Desktop layout
  return (
    <div style={{ display: 'flex', flex: 1, height: 'calc(100vh - 60px)', position: 'relative' }}>
      <Toast toast={toast} />

      {/* Products */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ArticleListing
          products={displayedProducts}
          onProductClick={addToCart}
          itemsPerRow={itemsPerRow}
          itemSize={itemSize}
        />
        
        {/* Mobile Footer */}
        {isMobile && (
          <OrderFooter
            total={total}
            style={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}
            leftButton={{
              label: MESSAGES.CLEAR_CART,
              onClick: () => { clearCart(); setShowOverview(false); },
              color: '#f44336'
            }}
            rightButton={{
              label: MESSAGES.OVERVIEW,
              onClick: () => setShowOverview(true),
              color: '#4ecdc4'
            }}
          />
        )}
      </div>

      {/* Edit Panel or Cart - Desktop */}
      {showEdit ? (
        <div style={{ width: '35%', minWidth: '300px' }}>
          <EditPanel 
            profile={profile} 
            onSave={() => { setShowEdit(false); setEditSaveTrigger(t => t + 1); }} 
            onCancel={() => setShowEdit(false)} 
            onSaveEdit={() => { setShowEdit(false); setEditSaveTrigger(t => t + 1); }} 
            onItemsPerRowChange={handleItemsPerRowChange}
            onItemSizeChange={handleItemSizeChange}
            initialItemsPerRow={itemsPerRow}
            onLiveChange={handleLiveChange}
            onRefresh={handleRefreshProducts}
          />
        </div>
      ) : (
        !isMobile && (
          <div style={{ width: '35%', minWidth: '300px' }}>
            <Cart
              cart={cart}
              onAdd={addToCart}
              onRemove={removeFromCart}
              onClear={clearCart}
              onOrder={handleOrder}
              total={total}
              lastOrderTotal={lastOrderTotal}
            />
          </div>
        )
      )}
    </div>
  )
}